import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import GoogleButton from "../components/GoogleButton";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { COACH_ROW } from "../lib/brand";

const HERO = COACH_ROW;

export default function Register() {
  const { user, register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (user) return <Navigate to={user.onboarding_complete ? "/app" : "/onboarding"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.password.length < 6) { toast.error("Fill all fields (password min 6 chars)."); return; }
    setBusy(true);
    try { await register(form.name, form.email, form.password); toast.success("Account created!"); navigate("/onboarding"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 sm:p-10 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-primary transition-colors">← Back to site</Link>
          <h1 className="font-display font-900 uppercase text-5xl tracking-tight mt-6">Start free</h1>
          <p className="text-muted-foreground mt-2 text-sm">Create your account and unlock your dashboard + 7-day challenge.</p>
          <div className="mt-7"><GoogleButton label="Sign up with Google" /></div>
          <div className="flex items-center gap-4 my-6"><div className="h-px bg-border flex-1" /><span className="text-xs uppercase tracking-widest text-muted-foreground">or</span><div className="h-px bg-border flex-1" /></div>
          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Name</Label><Input data-testid="register-name" value={form.name} onChange={set("name")} className="mt-2 h-12" /></div>
            <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Email</Label><Input data-testid="register-email" type="email" value={form.email} onChange={set("email")} className="mt-2 h-12" /></div>
            <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Password</Label><Input data-testid="register-password" type="password" value={form.password} onChange={set("password")} className="mt-2 h-12" placeholder="Min 6 characters" /></div>
            <button data-testid="register-submit" type="submit" disabled={busy} className="w-full bg-primary text-primary-foreground py-3.5 rounded-full text-xs uppercase tracking-[0.15em] font-bold hover:scale-[1.02] transition-transform disabled:opacity-60">{busy ? "Creating…" : "Create Account"}</button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">Already have an account? <Link to="/login" className="text-primary font-bold">Log in</Link></p>
        </div>
      </div>
      <div className="hidden lg:block relative overflow-hidden order-1 lg:order-2">
        <img src={HERO} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute bottom-12 right-12 text-right text-white">
          <div className="font-display font-900 uppercase text-6xl leading-[0.9]">Your best<br /><span className="text-primary">self starts</span><br />today</div>
        </div>
      </div>
    </div>
  );
}
